import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { FrontpageRoutes } from '@forvalter/frontpage/config';
import { Frontpage } from '@forvalter/frontpage/views/Frontpage';
import { ProjectDxboFrame } from '@klp/projectdxbo';

import {
    AuthCallbackView,
    LoginView,
    LogoutView,
    SilentCallbackView,
} from '@klp/auth';
import { AuthRoutes } from '@klpkonsern/auth-core/routes';
import { ReportRoutes } from '@klp/report/config';
import { Reports } from '@klp/report/views/ReportsView';
import { ContractManagementView } from '@klp/contractmanagement';
import { ContractManagementRoutes, ContractRegistrationRoutes } from './Routes';
import {
    ContractRegistrationView,
    ShareDocumentView,
    AssignResponsibleView,
    ContractRegistrationInstanceView,
} from '@klp/contractregistration';
import { EnvironmentRoutes, MiljoprofilRoutes } from '@klp/environment/config';
import EnvironmentView from '@klp/environment/views/EnvironmentView';
import Miljoprofil from '@klp/environment/views/MiljoprofilView';
import { AreaKeyFiguresRoutes, AreaKeyFigures } from '@klp/areakeyfigures';
import {
    EconomyKeyFiguresRoutes,
    EconomyKeyFigures,
} from '@klp/economykeyfigures';
import {
    Forvaltningsplan,
    ForvaltningsplanRoutes,
} from '@klp/forvaltningsplan';
import {
    ProsessDefinisjonDiagram,
    ProsjektListView,
    ProsjektView,
    ProsessDefinisjonDokumentasjon,
    ProsjektSammendragView,
    ProsjektRedirect,
} from '@klp/prosjekt';
import { ProsjektRoutes } from '@klp/prosjekt/config';
import { UserRoutes } from '@klp/user/config';
import Brukerliste from '@klp/user/components/Brukerliste';
import AdministrerBruker from '@klp/user/components/AdministrerBruker';
import { useCurrentUser } from '@klp/auth';
import ProsjektAvfallView from '@klp/environment/views/ProsjektAvfallView';
import ProsjektFullmaktView from '@klp/prosjekt/views/ProsjektFullmaktView';
import { reportMappingRoutes } from '@klp/reportMapping/config';
import { ReportMappingView } from '@klp/reportMapping/views/ReportMappingView';
import {
    LeverandorListView,
    LeverandorRoutes,
    LeverandorView,
} from '@klp/leverandor';
import { ReportMappingDocumentationView } from '@klp/reportMapping/views/ReportMappingDocumentationView';
import LeverandorProsessDefinisjonDokumentasjon from '@klp/leverandor/views/ProsessDefinisjonDokumentasjon';
import { LeverandorProsessDefinisjonDiagram } from '@klp/leverandor/views/ProsessDefinisjonDiagram';
import LeverandorReportView from '@klp/leverandor/views/LeverandorReportView';
import { LeverandorInherentRiskView } from '@klp/leverandor/views/LeverandorInherentRisk/LeverandorInherentRiskView';

export const AuthorisedRoutes: React.FC = () => {
    const { isAuthenticated, currentUserIsLoading } = useCurrentUser();

    // Guardian for authenticated access only (user has valid token and is logged in)
    if (!isAuthenticated) return null;
    if (currentUserIsLoading) return null; // Wait for user data to load, authorization is not determined yet

    return (
        <Switch>
            <Route
                path={AuthRoutes.logout}
                exact={true}
                component={LogoutView}
            />
            <Route
                path={AuthRoutes.mainLogin}
                exact={true}
                render={() => <LoginView mainLogin={true} />}
            />
            <Route path={AuthRoutes.login} exact={true} component={LoginView} />
            <Route
                path={AuthRoutes.authCallback}
                exact={true}
                component={AuthCallbackView}
            />
            <Route
                path={AuthRoutes.authSilentCallback}
                exact={true}
                component={SilentCallbackView}
            />
            <Route
                path={FrontpageRoutes.frontpage()}
                exact={true}
                component={Frontpage}
            />
            <Route
                path={ContractRegistrationRoutes.single(':kontraktId')}
                exact={true}
                component={ContractRegistrationView}
            />
            <Route
                path={ContractRegistrationRoutes.singleOld(':kontraktId')}
                exact={true}
                component={ContractRegistrationView}
            />
            <Route
                path={ContractRegistrationRoutes.singleInstance(
                    ':kontraktId',
                    ':prosessInstansId',
                )}
                exact={true}
                component={ContractRegistrationInstanceView}
            />
            <Route
                path={ContractRegistrationRoutes.shareDocument(':kontraktId')}
                exact={true}
                component={ShareDocumentView}
            />
            <Route
                path={ContractRegistrationRoutes.assignResponsible(
                    ':kontraktId',
                )}
                exact={true}
                component={AssignResponsibleView}
            />
            <Route
                path={ContractManagementRoutes.tab(':tab')}
                component={ContractManagementView}
            />
            <Route
                path={ContractManagementRoutes.moduleRoute()}
                component={ContractManagementView}
            />
            <Route
                path={ReportRoutes.reportList()}
                exact={true}
                component={Reports}
            />
            <Route
                path={EnvironmentRoutes.moduleRoute()}
                exact={true}
                component={EnvironmentView}
            />
            <Route
                path={MiljoprofilRoutes.moduleRoute()}
                exact={true}
                component={Miljoprofil}
            />
            <Route
                path={EnvironmentRoutes.prosjektAvfall()}
                exact={true}
                component={ProsjektAvfallView}
            />
            <Route
                path={AreaKeyFiguresRoutes.moduleRoute()}
                exact={true}
                component={AreaKeyFigures}
            />
            <Route
                path={EconomyKeyFiguresRoutes.moduleRoute()}
                exact={true}
                component={EconomyKeyFigures}
            />
            <Route
                path={ForvaltningsplanRoutes.moduleRoute()}
                exact={true}
                component={Forvaltningsplan}
            />
            <Route
                path={UserRoutes.moduleRoute()}
                exact={true}
                component={Brukerliste}
            />
            <Route
                path={UserRoutes.single(':id')}
                exact={true}
                component={AdministrerBruker}
            />
            <Route
                path={ProsjektRoutes.projectReport()}
                exact={true}
                component={ProjectDxboFrame}
            />
            <Route
                path={ProsjektRoutes.projectSchema()}
                exact={true}
                component={ProsjektListView}
            />
            <Route
                path={ProsjektRoutes.projectSchemaSingle(
                    ':prosessInstansId',
                    ':prosjektId',
                )}
                exact={true}
                component={ProsjektView}
            />
            <Route
                path={ProsjektRoutes.projectSummary(
                    ':prosessInstansId',
                    ':prosjektId',
                )}
                exact={true}
                component={ProsjektSammendragView}
            />
            <Route
                path={ProsjektRoutes.projectSchemaSingleOld(':prosjektId')}
                exact={true}
                component={ProsjektRedirect}
            />
            <Route
                path={ProsjektRoutes.processdefinitionDiagram(
                    ':processDefinitionId',
                )}
                exact={true}
                component={ProsessDefinisjonDiagram}
            />
            <Route
                path={ProsjektRoutes.processdefinitionDiagram()}
                exact={true}
                component={ProsessDefinisjonDiagram}
            />
            <Route
                path={ProsjektRoutes.projectSchemaDocumentationUrl()}
                exact={true}
                component={ProsessDefinisjonDokumentasjon}
            />
            <Route
                path={ProsjektRoutes.projectFullmakt()}
                exact={true}
                component={ProsjektFullmaktView}
            />
            <Route
                path={reportMappingRoutes.documentationRoute()}
                exact={true}
                component={ReportMappingDocumentationView}
            />
            <Route
                path={reportMappingRoutes.moduleRoute()}
                exact={true}
                component={ReportMappingView}
            />
            <Route
                path={LeverandorRoutes.leverandorSchema()}
                exact={true}
                component={LeverandorListView}
            />
            <Route
                path={LeverandorRoutes.leverandorReport()}
                exact={true}
                component={LeverandorReportView}
            />
            <Route
                path={LeverandorRoutes.leverandorSchemaSingle(
                    ':prosessInstansId',
                )}
                exact={true}
                component={LeverandorView}
            />
            <Route
                path={LeverandorRoutes.leverandorInherentRisk()}
                exact={true}
                component={LeverandorInherentRiskView}
            />
            <Route
                path={LeverandorRoutes.processdefinitionDiagram(
                    ':processDefinitionId',
                )}
                exact={true}
                component={LeverandorProsessDefinisjonDiagram}
            />
            <Route
                path={LeverandorRoutes.processdefinitionDiagram()}
                exact={true}
                component={LeverandorProsessDefinisjonDiagram}
            />
            <Route
                path={LeverandorRoutes.leverandorSchemaDocumentationUrl()}
                exact={true}
                component={LeverandorProsessDefinisjonDokumentasjon}
            />
            <Redirect to={FrontpageRoutes.frontpage()} />
        </Switch>
    );
};
